import type { ComputedRef, Ref } from 'vue'
import { emitter } from '@/plugins/emitter'
import { buildSummarizeFetcher } from '@/proofreading/phase2/summarize-api'

type SummaryItem = { text: string; length: number }

type SummarizePreviewState = {
  visible: boolean
  loading: boolean
  errorMessage: string
  rateLimited: boolean
  summaries: SummaryItem[]
  abortController: AbortController | null
  applied: boolean
}

type AxiosLike = {
  post: (
    url: string,
    data: unknown,
    config?: { timeout?: number }
  ) => Promise<{ data: Record<string, unknown> }>
  isCancel?: (err: unknown) => boolean
}

type ExtrasDeps = {
  reason: { value: string }
  summarizePreview: SummarizePreviewState
  summarizeTargetLength: ComputedRef<number>
  summarizePreviewFirst: ComputedRef<SummaryItem | null>
  summarizeModalVisible: Ref<boolean>
  isSendMail: Ref<boolean>
  correctSentence: Ref<string>
  aiGeneratedExampleReason: Ref<string>
  aiGeneratedExampleReasonFull: Ref<string>
  isAuthenticated: ComputedRef<boolean>
  user: Ref<{ uid: string } | null>
  globalVar: { isYagiPrime: boolean }
  cookies: {
    get: (key: string) => string | undefined
    set?: (
      keyName: string,
      value: unknown,
      expireTimes?: number | string | null,
      path?: string | null,
      domain?: string | null
    ) => void
  }
  axios: AxiosLike
  varuna: { endPoints: Record<string, string> }
  message: (opts: Record<string, unknown>) => unknown
  emit: (e: 'update:reason', value: string) => void
  getAiUsageCount: () => void
  vm: unknown
}

export function createReasonAiMyprExtras(deps: ExtrasDeps) {
  function openSummarizeModal() {
    if (!deps.reason.value || deps.reason.value.length < 50) {
      deps.message({
        message: '短縮提案を利用するには、もう少し文章を書いてください。',
        type: 'info'
      })
      return
    }
    deps.summarizeModalVisible.value = true
  }

  function applySummarize(newText: string) {
    if (typeof newText !== 'string' || !newText.trim()) return
    deps.emit('update:reason', newText)
    deps.message({
      message: '短縮した文章を反映しました。必要に応じて編集してください。',
      type: 'success'
    })
  }

  function reportProofreadEvent(
    action: string,
    extra?: { candidateIndex?: number }
  ) {
    const uid = deps.user.value?.uid || null
    const endpoint = deps.varuna.endPoints?.proofreadEvent
    if (!uid || !endpoint) return
    const payload: Record<string, unknown> = {
      uuid: uid,
      feature: 'summarize',
      action
    }
    if (extra && typeof extra.candidateIndex === 'number') {
      payload.candidate_index = extra.candidateIndex
    }
    try {
      deps.axios.post(endpoint, payload, { timeout: 5000 }).catch(() => {})
    } catch {
      /* noop */
    }
  }

  function loadSummarizePreview() {
    deps.summarizePreview.applied = false
    if (deps.summarizePreview.summaries.length > 0) {
      deps.summarizePreview.visible = true
      return
    }
    if (!deps.reason.value || deps.reason.value.length < 50) {
      deps.summarizePreview.errorMessage =
        '文章が短いため、短縮提案の対象外です。'
      deps.summarizePreview.visible = true
      return
    }
    const fetcher = buildSummarizeFetcher(deps.vm)
    if (!fetcher) {
      deps.summarizePreview.errorMessage =
        '短縮機能が利用できません。時間をおいて再度お試しください。'
      deps.summarizePreview.visible = true
      return
    }
    deps.summarizePreview.visible = true
    deps.summarizePreview.loading = true
    deps.summarizePreview.errorMessage = ''
    deps.summarizePreview.rateLimited = false
    const controller =
      typeof AbortController !== 'undefined' ? new AbortController() : null
    deps.summarizePreview.abortController = controller
    fetcher({
      text: deps.reason.value,
      targetLength: deps.summarizeTargetLength.value,
      variationCount: 3,
      context: '志望動機',
      signal: controller ? controller.signal : null
    })
      .then((summaries: SummaryItem[]) => {
        deps.summarizePreview.loading = false
        deps.summarizePreview.abortController = null
        deps.summarizePreview.summaries = Array.isArray(summaries)
          ? summaries
          : []
        if (deps.summarizePreview.summaries.length === 0) {
          deps.summarizePreview.errorMessage =
            '短縮案を生成できませんでした。文章を少し編集してから再度お試しください。'
        }
      })
      .catch((err: { response?: { status?: number }; code?: string }) => {
        deps.summarizePreview.loading = false
        deps.summarizePreview.abortController = null
        if (deps.axios.isCancel && deps.axios.isCancel(err)) return
        const status = err && err.response && err.response.status
        if (status === 401 || err.code === 'UNAUTHENTICATED') {
          deps.summarizePreview.errorMessage = 'ログインが必要です。'
        } else if (status === 429) {
          deps.summarizePreview.rateLimited = true
        } else if (status === 502) {
          deps.summarizePreview.errorMessage =
            'AIサービスが一時的に利用できません。しばらくしてから再度お試しください。'
        } else {
          deps.summarizePreview.errorMessage = '短縮案の取得に失敗しました。'
        }
      })
  }

  function closeSummarizePreview() {
    if (deps.summarizePreview.abortController) {
      deps.summarizePreview.abortController.abort()
      deps.summarizePreview.abortController = null
    }
    if (
      !deps.summarizePreview.applied &&
      deps.summarizePreview.summaries.length > 0
    ) {
      reportProofreadEvent('dismiss')
    }
    deps.summarizePreview.visible = false
  }

  function openYagiprimeFromPreview() {
    const routeId = deps.cookies.get('_YAGISH_YAGIPRIME_ROUTE_ID') || ''
    deps.cookies.set?.(
      '_YAGISH_YAGIPRIME_ROUTE_ID',
      routeId + '#reason_long',
      null,
      null,
      'yagish.jp'
    )
    deps.cookies.set?.(
      '_YAGISH_YAGIPRIME_ROUTE_ID',
      routeId + '#reason_long',
      null,
      null,
      'localhost'
    )
    emitter.emit('show-yagiprime-payment-dialog', 'reason_long')
  }

  function applySummarizePreview() {
    const first = deps.summarizePreviewFirst.value
    if (!first || !first.text) return
    deps.emit('update:reason', first.text)
    deps.summarizePreview.applied = true
    reportProofreadEvent('accept', { candidateIndex: 0 })
    deps.summarizePreview.visible = false
    deps.message({
      message: '短縮した文章を反映しました。',
      type: 'success'
    })
  }

  function updateAIExampleDisplay() {
    if (deps.aiGeneratedExampleReasonFull.value) {
      if (deps.isAuthenticated.value && deps.globalVar.isYagiPrime) {
        deps.aiGeneratedExampleReason.value =
          deps.aiGeneratedExampleReasonFull.value
      } else {
        deps.aiGeneratedExampleReason.value =
          deps.aiGeneratedExampleReasonFull.value.substring(0, 100) + '...'
      }
    }
  }

  function loadAIExampleFromLocalStorage() {
    try {
      const storageKey = 'ai_generated_reason_example'
      const savedData = localStorage.getItem(storageKey)
      if (!savedData) return
      const parsedData = JSON.parse(savedData) as {
        timestamp?: number
        text?: string
      }
      const now = Date.now()
      const expirationTime = 7 * 24 * 60 * 60 * 1000
      if (
        parsedData.timestamp &&
        now - parsedData.timestamp < expirationTime &&
        parsedData.text
      ) {
        deps.aiGeneratedExampleReasonFull.value = parsedData.text
        updateAIExampleDisplay()
      } else {
        localStorage.removeItem(storageKey)
      }
    } catch {
      /* noop */
    }
  }

  function authStateChanged() {
    if (!deps.user.value) return
    const body = new FormData()
    body.append('uuid', deps.user.value.uid)
    deps.getAiUsageCount()
    deps.axios
      .post(deps.varuna.endPoints.isSendCorrectionMail, body, {
        timeout: 60000
      })
      .then((response) => {
        const data = response.data as { success?: boolean; sentence?: string }
        deps.isSendMail.value = !!data.success
        deps.correctSentence.value = data.sentence || ''
      })
    updateAIExampleDisplay()
  }

  function insertSentence() {
    if (!deps.globalVar.isYagiPrime) {
      localStorage.setItem('correctSentence', deps.correctSentence.value)
      emitter.emit('open-yagiprime-payment-dialog')
      return
    }
    deps.emit('update:reason', deps.correctSentence.value)
    if (deps.user.value) {
      const body = new FormData()
      body.append('uuid', deps.user.value.uid)
      deps.axios.post(deps.varuna.endPoints.insertedSentence, body, {
        timeout: 60000
      })
    }
    deps.message({ message: '追加しました', type: 'success' })
  }

  return {
    openSummarizeModal,
    applySummarize,
    loadSummarizePreview,
    closeSummarizePreview,
    openYagiprimeFromPreview,
    applySummarizePreview,
    loadAIExampleFromLocalStorage,
    updateAIExampleDisplay,
    authStateChanged,
    insertSentence,
    showYagiprimeForSummarize: loadSummarizePreview
  }
}
